import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  InstagramLogo, LinkedinLogo, FacebookLogo, CheckCircle, WarningCircle,
  ArrowsClockwise, Clock, FunnelSimple, ArrowClockwise, Eye, Trash,
  ArrowSquareOut, Copy, CalendarBlank, ChartBar, Info
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = { instagram: InstagramLogo, facebook: FacebookLogo, linkedin: LinkedinLogo };
const PLATFORM_COLORS = { instagram: '#E4405F', facebook: '#1877F2', linkedin: '#0A66C2' };

const STATUS_CONFIG = {
  published: { label: 'Published', color: '#22c55e', icon: CheckCircle },
  failed: { label: 'Failed', color: '#ef4444', icon: WarningCircle },
  retried: { label: 'Retried', color: '#f59e0b', icon: ArrowClockwise },
  pending: { label: 'Pending', color: '#8b5cf6', icon: Clock },
  scheduled: { label: 'Scheduled', color: '#3b82f6', icon: CalendarBlank },
};

export default function PostHistoryPage() {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ platform: '', status: '' });
  const [retrying, setRetrying] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchData(); }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.platform) params.platform = filter.platform;
      if (filter.status) params.status = filter.status;
      const [hRes, sRes] = await Promise.all([
        axios.get(`${API}/api/publishing/history`, { params }),
        axios.get(`${API}/api/publishing/stats`),
      ]);
      setAttempts(hRes.data.attempts || []);
      setStats(sRes.data);
    } catch {}
    setLoading(false);
  };

  const retryAttempt = async (attemptId) => {
    setRetrying(attemptId);
    try {
      await axios.post(`${API}/api/publishing/retry/${attemptId}`);
      await fetchData();
    } catch {}
    setRetrying(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="post-history-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Post History</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Track every publish attempt, retry failures, view logs.</p>
          </div>
          <button onClick={() => navigate('/dashboard/publish')} data-testid="new-post-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: '#c8f060', color: '#080808' }}>New Post</button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Posts', value: stats.total_attempts, color: '#f5f0e8' },
              { label: 'Published', value: stats.published, color: '#22c55e' },
              { label: 'Failed', value: stats.failed, color: '#ef4444' },
              { label: 'Success Rate', value: `${stats.success_rate}%`, color: '#c8f060' },
            ].map(s => (
              <div key={s.label} className="p-3.5 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.25)' }}>{s.label}</p>
                <p className="text-xl font-semibold mt-1" style={{ color: s.color, fontFamily: "'Instrument Serif', serif" }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Platform stats */}
        {stats?.by_platform && (
          <div className="flex gap-3">
            {Object.entries(stats.by_platform).map(([p, data]) => {
              const Icon = PLATFORM_ICONS[p];
              const color = PLATFORM_COLORS[p];
              return (
                <div key={p} className="flex items-center gap-2.5 px-3 py-2 rounded-lg" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                  {Icon && <Icon size={16} weight="fill" style={{ color }} />}
                  <div>
                    <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{data.total} posts</p>
                    <p className="text-[11px] font-semibold" style={{ color }}>{data.rate}% success</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2">
          <FunnelSimple size={14} style={{ color: 'rgba(245,240,232,0.2)' }} />
          <select value={filter.platform} onChange={e => setFilter(prev => ({ ...prev, platform: e.target.value }))} data-testid="filter-platform"
            className="px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)', outline: 'none' }}>
            <option value="">All Platforms</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="linkedin">LinkedIn</option>
          </select>
          <select value={filter.status} onChange={e => setFilter(prev => ({ ...prev, status: e.target.value }))} data-testid="filter-status"
            className="px-2.5 py-1.5 rounded-lg text-xs"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)', outline: 'none' }}>
            <option value="">All Statuses</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
            <option value="retried">Retried</option>
          </select>
          <button onClick={fetchData} className="p-1.5 rounded-lg hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.3)' }}><ArrowsClockwise size={14} /></button>
        </div>

        {/* Attempts list */}
        {loading ? (
          <div className="space-y-2">
            {[1,2,3,4].map(i => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : attempts.length === 0 ? (
          <div className="py-16 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }} data-testid="history-empty">
            <ChartBar size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.08)' }} />
            <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No publish attempts yet</p>
            <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>Publish your first post to see it here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attempts.map(attempt => {
              const status = STATUS_CONFIG[attempt.status] || STATUS_CONFIG.pending;
              const Icon = PLATFORM_ICONS[attempt.platform];
              const pColor = PLATFORM_COLORS[attempt.platform] || '#888';
              const isExpanded = expandedId === attempt.attempt_id;

              return (
                <motion.div key={attempt.attempt_id} layout
                  className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}
                  data-testid={`attempt-${attempt.attempt_id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : attempt.attempt_id)}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${pColor}15` }}>
                      {Icon && <Icon size={16} weight="fill" style={{ color: pColor }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>
                        {attempt.platform?.charAt(0).toUpperCase() + attempt.platform?.slice(1)} Post
                      </p>
                      <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                        {new Date(attempt.completed_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ background: `${status.color}15`, color: status.color }}>
                        <status.icon size={10} weight="fill" />
                        {status.label}
                      </span>
                      {attempt.published_url && (
                        <a href={attempt.published_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          className="p-1 rounded hover:bg-white/10" style={{ color: 'rgba(245,240,232,0.3)' }}>
                          <ArrowSquareOut size={13} />
                        </a>
                      )}
                      {attempt.status === 'failed' && attempt.retryable && (
                        <button onClick={e => { e.stopPropagation(); retryAttempt(attempt.attempt_id); }}
                          disabled={retrying === attempt.attempt_id}
                          data-testid={`retry-${attempt.attempt_id}`}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium"
                          style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.15)' }}>
                          {retrying === attempt.attempt_id ? <ArrowsClockwise size={10} className="animate-spin" /> : <ArrowClockwise size={10} />}
                          Retry
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="overflow-hidden">
                      <div className="px-4 pb-4 space-y-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                        <div className="grid grid-cols-2 gap-2 pt-3">
                          {attempt.error_message && (
                            <div className="col-span-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.06)' }}>
                              <p className="text-[10px] font-bold uppercase" style={{ color: 'rgba(239,68,68,0.5)' }}>Error</p>
                              <p className="text-xs mt-0.5" style={{ color: 'rgba(239,68,68,0.8)' }}>{attempt.error_message}</p>
                            </div>
                          )}
                          {attempt.suggested_action && (
                            <div className="col-span-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.06)' }}>
                              <p className="text-[10px] font-bold uppercase" style={{ color: 'rgba(245,158,11,0.5)' }}>Suggested Action</p>
                              <p className="text-xs mt-0.5" style={{ color: 'rgba(245,158,11,0.8)' }}>{attempt.suggested_action}</p>
                            </div>
                          )}
                          {attempt.published_url && (
                            <div className="col-span-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(34,197,94,0.06)' }}>
                              <p className="text-[10px] font-bold uppercase" style={{ color: 'rgba(34,197,94,0.5)' }}>Published URL</p>
                              <a href={attempt.published_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: '#22c55e' }}>{attempt.published_url}</a>
                            </div>
                          )}
                          <div className="px-3 py-2 rounded-lg" style={{ background: '#0a0a0a' }}>
                            <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>Retries</p>
                            <p className="text-xs font-medium" style={{ color: '#f5f0e8' }}>{attempt.retry_count || 0}</p>
                          </div>
                          <div className="px-3 py-2 rounded-lg" style={{ background: '#0a0a0a' }}>
                            <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>Attempt ID</p>
                            <p className="text-[10px] font-mono truncate" style={{ color: 'rgba(245,240,232,0.3)' }}>{attempt.attempt_id}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
